import React, { useEffect, useMemo, useState } from 'react';
import { ButtonGroup } from '../../components/forms/ButtonGroup';
import { Input } from '../../components/forms/Input';
import { Select } from '../../components/forms/Select';
import { SelectSearch } from '../../components/forms/SelectSearch';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { AutomationApiService } from '../services/automationApi';
import type { AvailableDataSource } from './DynamicFieldValue';
import { DynamicFieldValue } from './DynamicFieldValue';

interface EventType {
	key: string;
	label: string;
	description: string;
	constraintField?: string;
	outputSchema: Record<string, unknown>;
}

interface WaitForEventStepConfigProps {
	config: Record<string, unknown>;
	setConfig: (config: Record<string, unknown>) => void;
	getAvailableDataSources: () => Array<AvailableDataSource>;
	getDisplayText: (value: unknown) => string;
}

export function WaitForEventStepConfig({
	config,
	setConfig,
	getAvailableDataSources,
	getDisplayText,
}: WaitForEventStepConfigProps) {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [eventTypes, setEventTypes] = useState<Array<EventType>>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		loadEventTypes();
	}, []);

	const loadEventTypes = async () => {
		try {
			const apiService = new AutomationApiService();
			const types = await apiService.getEventTypes();
			setEventTypes(types as Array<EventType>);
		} catch (error) {
			console.error('Failed to load event types:', error);
		} finally {
			setLoading(false);
		}
	};

	const selectedEventType = eventTypes.find(
		(et) => et.key === config['eventType'],
	);

	// Filter available data sources to only show fields that match the constraint field
	// This provides smart linking - when waiting for call.completed (constraintField: 'callId'),
	// only show step outputs that have a 'callId' field
	const filteredDataSources = useMemo((): Array<AvailableDataSource> => {
		const allSources = getAvailableDataSources();
		const constraintField = selectedEventType?.constraintField;

		if (constraintField == null) {
			return allSources;
		}

		// Filter each source's fields to only include those matching the constraint field
		return allSources
			.map((source) => {
				const matchingFields = source.fields.filter((field) => {
					// Check if the field key ends with the constraint field name
					// e.g., for constraintField 'callId', match 'step1.callId' or just 'callId'
					const fieldKey = field.key;
					const fieldName = fieldKey.includes('.')
						? (fieldKey.split('.').pop() ?? '')
						: fieldKey;

					return fieldName === constraintField;
				});

				if (matchingFields.length === 0) {
					return null;
				}

				return {
					...source,
					fields: matchingFields,
				};
			})
			.filter((source): source is AvailableDataSource => source != null);
	}, [getAvailableDataSources, selectedEventType]);

	const handleEventTypeChange = (eventKey: string) => {
		// Clear the constraint when event type changes since the constraintField may differ
		setConfig({
			...config,
			eventType: eventKey,
			constraint: '',
		});
	};

	// Group event types by prefix (e.g., "email.complained" → "Email" group)
	const eventTypeGroups = React.useMemo(() => {
		const grouped = new Map<
			string,
			Array<{ value: string; label: string; subtitle: string }>
		>();

		eventTypes.forEach((et) => {
			// Extract prefix before the dot (e.g., "email" from "email.complained")
			const prefix = et.key.includes('.')
				? (et.key.split('.')[0] ?? 'other')
				: 'other';

			if (!grouped.has(prefix)) {
				grouped.set(prefix, []);
			}

			grouped.get(prefix)?.push({
				value: et.key,
				label: et.label,
				subtitle: et.description,
			});
		});

		// Convert to groups array, capitalize prefix for display
		return Array.from(grouped.entries()).map(([prefix, options]) => ({
			key: prefix,
			label: t(prefix.charAt(0).toUpperCase() + prefix.slice(1)),
			options,
		}));
	}, [eventTypes, t]);

	const hasTimeout =
		config['timeout'] != null && (config['timeout'] as number) > 0;

	const handleTimeoutToggle = (enabled: 'enabled' | 'disabled') => {
		if (enabled === 'enabled') {
			setConfig({
				...config,
				timeout: 1,
				timeoutUnit: (config['timeoutUnit'] as string) ?? 'hours',
			});
		} else {
			setConfig({
				...config,
				timeout: 0,
			});
		}
	};

	const handleTimeoutChange = (timeout: number) => {
		setConfig({
			...config,
			timeout,
		});
	};

	const handleTimeoutUnitChange = (timeoutUnit: string) => {
		setConfig({
			...config,
			timeoutUnit,
		});
	};

	const constraintDescription = useMemo(() => {
		if (selectedEventType?.constraintField != null) {
			return `${t('Select a')} ${selectedEventType.constraintField} ${t('from a previous step to match when the event occurs.')}`;
		}

		return t(
			'The identifier to match when the event occurs (e.g., call ID, email ID). Use dynamic values to match against data from previous steps.',
		);
	}, [selectedEventType, t]);

	const noMatchingFieldsWarning = useMemo(() => {
		if (selectedEventType?.constraintField != null) {
			return `${t('No previous steps output a')} ${selectedEventType.constraintField}. ${t('Add a step that produces this field before the wait step.')}`;
		}

		return '';
	}, [selectedEventType, t]);

	return (
		<div className="space-y-6">
			{/* Event Type Selection */}
			<div>
				<label
					className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
				>
					{t('Event Type')} <span className="text-danger">*</span>
				</label>
				{loading ? (
					<div className={`text-sm ${colors.classes.textSecondary}`}>
						{t('Loading event types...')}
					</div>
				) : (
					<>
						<SelectSearch
							mode="static"
							value={(config['eventType'] as string) ?? ''}
							onChange={handleEventTypeChange}
							placeholder={t('Search event types...')}
							groups={eventTypeGroups}
							emptyMessage={t('No event types available')}
							fullWidth
						/>
						{selectedEventType != null && (
							<p className={`text-sm mt-2 ${colors.classes.textSecondary}`}>
								{t(selectedEventType.description)}
							</p>
						)}
					</>
				)}
			</div>

			{/* Constraint Value with built-in static/dynamic toggle */}
			<div>
				<DynamicFieldValue
					label={t('Event Constraint')}
					value={(config['constraint'] as string) ?? ''}
					onChange={(value) => setConfig({ ...config, constraint: value })}
					dataType="string"
					placeholder={t('Enter constraint value or select from data')}
					required={true}
					description={constraintDescription}
					availableData={filteredDataSources}
					displayTransform={getDisplayText}
				/>
				{selectedEventType?.constraintField != null &&
					filteredDataSources.length === 0 && (
						<p className={`text-sm mt-2 ${colors.classes.textWarning}`}>
							{noMatchingFieldsWarning}
						</p>
					)}
			</div>

			{/* Timeout Configuration with Toggle */}
			<div>
				<label
					className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
				>
					{t('Set Timeout')}
				</label>
				<ButtonGroup
					name="timeoutEnabled"
					options={[
						{ label: t('No Timeout'), value: 'disabled' },
						{ label: t('Set Timeout'), value: 'enabled' },
					]}
					value={hasTimeout ? 'enabled' : 'disabled'}
					onChange={(value) =>
						handleTimeoutToggle(value as 'enabled' | 'disabled')
					}
					fullWidth
					size="sm"
				/>
				{hasTimeout && (
					<div className="mt-4">
						<label
							className={`text-sm font-medium mb-2 block ${colors.classes.textSecondary}`}
						>
							{t('Timeout Duration')}
						</label>
						<div className="flex gap-2">
							<div className="flex-1">
								<Input
									type="number"
									value={(config['timeout'] as number) ?? 1}
									onChange={(e) =>
										handleTimeoutChange(parseInt(e.target.value) || 1)
									}
									placeholder="1"
									min={1}
									fullWidth
								/>
							</div>
							<div className="w-32">
								<Select
									value={(config['timeoutUnit'] as string) ?? 'hours'}
									onChange={handleTimeoutUnitChange}
									options={[
										{ value: 'seconds', label: t('Seconds') },
										{ value: 'minutes', label: t('Minutes') },
										{ value: 'hours', label: t('Hours') },
										{ value: 'days', label: t('Days') },
									]}
									fullWidth
								/>
							</div>
						</div>
						<p className={`text-sm mt-2 ${colors.classes.textSecondary}`}>
							{t(
								'If the event does not occur within this time, the step will follow the "timeout" connection path',
							)}
						</p>
					</div>
				)}
				{!hasTimeout && (
					<p className={`text-sm mt-2 ${colors.classes.textSecondary}`}>
						{t(
							'The step will wait indefinitely until the event occurs. Add a connection with label "timeout" to handle timeout cases.',
						)}
					</p>
				)}
			</div>

			{/* Output Schema Information */}
			{selectedEventType != null && selectedEventType.outputSchema != null && (
				<div className={`p-4 rounded-lg ${colors.classes.bgSecondary}`}>
					<h4
						className={`text-sm font-medium mb-2 ${colors.classes.textPrimary}`}
					>
						{t('Available Output Fields')}
					</h4>
					<p className={`text-sm mb-3 ${colors.classes.textSecondary}`}>
						{t(
							'These fields will be available to downstream steps when this event occurs:',
						)}
					</p>
					<div className="space-y-2">
						{Object.entries(selectedEventType.outputSchema).map(
							([key, schema]: [string, unknown]) => {
								const schemaObj = schema as Record<string, unknown>;

								return (
									<div
										key={key}
										className="flex items-start justify-between gap-3"
									>
										<div className="flex-1">
											<div
												className={`text-sm font-medium ${colors.classes.textPrimary}`}
											>
												{t(String(schemaObj['label'] ?? key))}
											</div>
											{schemaObj['description'] != null && (
												<div
													className={`text-xs ${colors.classes.textSecondary}`}
												>
													{t(String(schemaObj['description']))}
												</div>
											)}
										</div>
										<span
											className={`text-xs ${colors.classes.textSecondary} whitespace-nowrap`}
										>
											{t(String(schemaObj['type'] ?? 'string'))}
										</span>
									</div>
								);
							},
						)}
					</div>
				</div>
			)}
		</div>
	);
}
